import {
  Component,
  Input,
  ContentChildren,
  QueryList,
  Directive,
  TemplateRef,
  ContentChild,
  AfterContentChecked,
  Output,
  EventEmitter
} from '@angular/core';
import { FishTabsetConfig } from './tabset-config';

let nextId = 0;

/**
 * This directive should be used to wrap tab titles that need to contain HTML markup or other directives.
 */

export class FishTabTitle {
  constructor(public templateRef: TemplateRef<any>) { }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{ selector: 'ng-template[fishTabTitle]' }, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: TemplateRef, },
];
}

/**
 * This directive must be used to wrap content to be displayed in a tab.
 */

export class FishTabContent {
  constructor(public templateRef: TemplateRef<any>) { }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{ selector: 'ng-template[fishTabContent]' }, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: TemplateRef, },
];
}

/**
 * A directive representing an individual tab.
 */

export class FishTab {
  /**
   * Unique tab identifier. Must be unique for the entire document for proper accessibility support.
   */
   id: string = `fish-tab-${nextId++}`;
  /**
   * Simple (string only) title. Use the "FishTabTitle" directive for more complex use-cases.
   */
   title: string;
  /**
   * Allows toggling disabled state of a given state. Disabled tabs can't be selected.
   */
   disabled = false;

   contentTpl: FishTabContent;
   titleTpl: FishTabTitle;
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{ selector: 'fish-tab' }, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'id': [{ type: Input },],
'title': [{ type: Input },],
'disabled': [{ type: Input },],
'contentTpl': [{ type: ContentChild, args: [FishTabContent, ] },],
'titleTpl': [{ type: ContentChild, args: [FishTabTitle, ] },],
};
}

/**
 * The payload of the change event fired right before the tab change
 */
export interface FishTabChangeEvent {
  /**
   * Id of the currently active tab
   */
  activeId: string;

  /**
   * Id of the newly selected tab
   */
  nextId: string;

  /**
   * Function that will prevent tab switch if called
   */
  preventDefault: () => void;
}

/**
 * A component that makes it easy to create tabbed interface.
 */

export class FishTabset implements AfterContentChecked {
   tabs: QueryList<FishTab>;

  /**
   * An identifier of an initially selected (active) tab. Use the "select" method to switch a tab programmatically.
   */
   activeId: string;

  /**
   * Whether the closed tabs should be hidden without destroying them
   */
   destroyOnHide: boolean = true;

  /**
   * The horizontal alignment of the nav with flexbox utilities. Can be one of 'start', 'center' or 'end'
   */
   justify: 'start' | 'center' | 'end';

  /**
   * Type of navigation to be used for tabs. Can be one of 'tabs' or 'pills'.
   */
   type: 'tabs' | 'pills';

  /**
   * A tab change event fired right before the tab selection happens. See FishTabChangeEvent for payload details
   */
   tabChange = new EventEmitter<FishTabChangeEvent>();

  constructor(config: FishTabsetConfig) {
    this.type = config.type;
    this.justify = config.justify;
  }

  /**
   * Selects the tab with the given id and shows its associated pane.
   * Any other tab that was previously selected becomes unselected and its associated pane is hidden.
   */
  select(tabId: string) {
    let selectedTab = this._getTabById(tabId);
    if (selectedTab && !selectedTab.disabled && this.activeId !== selectedTab.id) {
      let defaultPrevented = false;

      this.tabChange.emit(
        { activeId: this.activeId, nextId: selectedTab.id, preventDefault: () => { defaultPrevented = true; } });

      if (!defaultPrevented) {
        this.activeId = selectedTab.id;
      }
    }
  }

  ngAfterContentChecked() {
    // auto-correct activeId that might have been set incorrectly as input
    let activeTab = this._getTabById(this.activeId);
    this.activeId = activeTab ? activeTab.id : (this.tabs.length ? this.tabs.first.id : null);
  }

  private _getTabById(id: string): FishTab {
    let tabsWithId: FishTab[] = this.tabs.filter(tab => tab.id === id);
    return tabsWithId.length ? tabsWithId[0] : null;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'fish-tabset',
  exportAs: 'fishTabset',
  template: `
    <ul [class]="'nav nav-' + type + ' justify-content-' + justify" role="tablist">
      <li class="nav-item" *ngFor="let tab of tabs">
        <a [id]="tab.id" class="nav-link" [class.active]="tab.id === activeId" [class.disabled]="tab.disabled"
          href (click)="!!select(tab.id)" role="tab" [attr.tabindex]="(tab.disabled ? '-1': undefined)"
          [attr.aria-controls]="(!destroyOnHide || tab.id === activeId ? tab.id + '-panel' : null)"
          [attr.aria-expanded]="tab.id === activeId" [attr.aria-disabled]="tab.disabled">
          {{tab.title}}<ng-template [ngTemplateOutlet]="tab.titleTpl?.templateRef"></ng-template>
        </a>
      </li>
    </ul>
    <div class="tab-content">
      <ng-template ngFor let-tab [ngForOf]="tabs">
        <div
          class="tab-pane {{tab.id === activeId ? 'active' : null}}"
          *ngIf="!destroyOnHide || tab.id === activeId"
          role="tabpanel"
          [attr.aria-labelledby]="tab.id" id="{{tab.id}}-panel"
          [attr.aria-expanded]="tab.id === activeId">
          <ng-template [ngTemplateOutlet]="tab.contentTpl.templateRef"></ng-template>
        </div>
      </ng-template>
    </div>
  `
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: FishTabsetConfig, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'tabs': [{ type: ContentChildren, args: [FishTab, ] },],
'activeId': [{ type: Input },],
'destroyOnHide': [{ type: Input },],
'justify': [{ type: Input },],
'type': [{ type: Input },],
'tabChange': [{ type: Output },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
